import React, { useState, useEffect, ReactElement } from 'react'

import {
  Modal,
  HeadingMedium,
  ImageCarouselApp,
  BodyText,
  Button,
  StyleItIcon,
  CareForItIcon,
  BagIcon,
  CTALink,
  ArrowLeftIcon,
  FadeSwap,
  Accordion,
  AccordionItem,
} from '@pb/components'
import {
  useRequest,
  getLocale,
  trackViewEvent,
  trackEvent,
  getLabelFromStorage,
} from '@pb/utils'
import { useFeature } from '@optimizely/react-sdk'
import { useSelector } from 'react-redux'

import appSelectors from 'App/duck/selectors'
// TODO clean up optimizely events https://hmgroup.atlassian.net/browse/BTSO-2109
import optimizelyConstants, {
  CLOSET_DETAIL_PAGEVIEW,
  CLICK_MY_CLOSET_STYLE_IT,
  CLICK_MY_CLOSET_CARE_FOR_IT,
  CLICK_MY_CLOSET_BUY_IT_AGAIN,
} from 'constants/optimizely'
import { myclosetDetailPrepData } from 'features/favourites/duck/normalize'
import { trackOptimizelyEvent } from 'helpers/optimizely'

import { MYCLOSET_DETAIL_TRACKING_EVENTS } from '../../tracking'
import { MyClosetDetailProps, MyClosetDetailResultProps } from '../../types'
import styles from './MyClosetDetail.scss'

const MyClosetDetail: React.FC<MyClosetDetailProps> = ({
  isOpen,
  articleCode,
  categoryName,
  onToggle,
}) => {
  const [showCareForit, setshowCareForit] = useState<boolean>(false)
  const [showStyleit, setShowStyleit] = useState<boolean>(false)
  const [myClosetDetail, setMyClosetDetail] = useState<MyClosetDetailResultProps>()

  const userBpid = useSelector(appSelectors.getUserBpid)

  const [isMyClosetStyleit, { showMyClosetStyleit }] = useFeature(
    optimizelyConstants.MY_CLOSET_FLAG,
  )
  const showMyclosetStyleitFlag =
    (isMyClosetStyleit && showMyClosetStyleit) ||
    showMyClosetStyleit === localStorage.getItem('showMyClosetStyleit')

  const { loading: isLoadingResult, data: myclosetDetailResult } = useRequest(
    `${process.env.MY_CLOSET_URL}/${getLocale('lowerCase')}/productinfo/${articleCode}`,
    {
      method: 'get',
    },
  )

  useEffect(() => {
    if (!isLoadingResult && myclosetDetailResult && Object.keys(myclosetDetailResult).length) {
      const prepData = myclosetDetailPrepData(myclosetDetailResult)
      setMyClosetDetail(prepData[0])
      trackViewEvent({
        ...MYCLOSET_DETAIL_TRACKING_EVENTS.myclosetDetailView,
        page_id: `${categoryName} Detail`,
        customer_bpid: userBpid,
      })
      trackOptimizelyEvent(CLOSET_DETAIL_PAGEVIEW)
    }
  }, [myclosetDetailResult, isLoadingResult])

  const handleToggle = (): void => {
    onToggle()
  }

  const handleBackButton = (): void => {
    setshowCareForit(false)
    setShowStyleit(false)
  }

  const renderCareForit = (copy, careit): ReactElement => {
    return (
      <Modal
        title={myClosetDetail?.name}
        closeButtonText="close"
        modalType="stickyRight"
        isOpen={isOpen}
        toggleModal={handleToggle}
        innerContainerClassName={styles.innerContainerCloset}
        modalHeadingClassName={styles.modalHeading}
        closeButtonSize="small"
        closeButtonClassName={styles.closeButton}
      >
        <CTALink
          className={styles.backBtn}
          Tag="button"
          iconElement={<ArrowLeftIcon />}
          onClick={handleBackButton}
          fullWidth
        />
        <div className={styles.careForitButton}>
          <Accordion
            accordionType="definition"
            toggleIconType="chevron"
            shouldAnimate
            iconSize="normal"
          >
            <AccordionItem headerLeftContent={copy}>
              <div className={styles.listItems}>
                {Object.keys(careit).map((key) => (
                  <li>{careit[key].value}</li>
                ))}
              </div>
            </AccordionItem>
          </Accordion>
        </div>
      </Modal>
    )
  }

  const handleCareForit = (): void => {
    setshowCareForit(true)
    trackEvent({
      ...MYCLOSET_DETAIL_TRACKING_EVENTS.myclosetLinkClicks,
      customer_bpid: userBpid,
      page_id: `${categoryName} detail`,
      event_id: 'care for it',
      event_label: 'care for it open',
    })
    trackOptimizelyEvent(CLICK_MY_CLOSET_CARE_FOR_IT)
  }

  const renderStyleit = (copy): ReactElement => {
    return (
      <Modal
        title={copy}
        closeButtonText="close"
        modalType="stickyRight"
        isOpen={isOpen}
        toggleModal={handleToggle}
        innerContainerClassName={styles.innerContainerCloset}
        modalHeadingClassName={styles.modalHeading}
        closeButtonSize="small"
        closeButtonClassName={styles.closeButton}
      >
        <CTALink
          className={styles.backBtn}
          Tag="button"
          iconElement={<ArrowLeftIcon />}
          onClick={handleBackButton}
          fullWidth
        />
        <BodyText className={styles.styleitText}>
          {getLabelFromStorage('mycloset.painteddoor.thankyou.body')}
        </BodyText>
      </Modal>
    )
  }

  const handleStyleit = (): void => {
    if (showMyclosetStyleitFlag) setShowStyleit(true)
    trackEvent({
      ...MYCLOSET_DETAIL_TRACKING_EVENTS.myclosetLinkClicks,
      customer_bpid: userBpid,
      page_id: `${categoryName} detail`,
      event_id: 'style it',
      event_label: 'style it open',
    })
    trackOptimizelyEvent(CLICK_MY_CLOSET_STYLE_IT)
  }

  const careForitBtn = (): ReactElement => {
    return (
      <Button
        Tag="button"
        size="large"
        className={styles.buttonContainer}
        secondary
        iconPosition="top"
        iconElement={<CareForItIcon size="xlarge" />}
        onClick={handleCareForit}
      >
        {getLabelFromStorage('mycloset.detail.careforit.button.title')}
      </Button>
    )
  }

  const styleItBtn = (): ReactElement => {
    return (
      <Button
        Tag="button"
        size="large"
        className={styles.buttonContainer}
        secondary
        iconPosition="top"
        iconElement={<StyleItIcon size="xlarge" />}
        onClick={handleStyleit}
      >
        {getLabelFromStorage('mycloset.detail.styleit.button.title')}
      </Button>
    )
  }

  const buyItAgainBtn = (): ReactElement => {
    return (
      <Button
        Tag="button"
        size="large"
        className={showMyclosetStyleitFlag ? styles.buyitAgainButton : styles.buttonContainer}
        secondary
        iconPosition="top"
        iconElement={<BagIcon size="large" />}
        onClick={() => {
          window.location.assign(`/${getLocale('lowerCase')}/productpage.${articleCode}.html`)
          trackEvent({
            ...MYCLOSET_DETAIL_TRACKING_EVENTS.myclosetLinkClicks,
            customer_bpid: userBpid,
            page_id: `${categoryName} detail`,
            event_id: 'buy it',
            event_label: 'buy it open',
          })
          trackOptimizelyEvent(CLICK_MY_CLOSET_BUY_IT_AGAIN)
        }}
      >
        {getLabelFromStorage('mycloset.detail.buyitagain.button.title')}
      </Button>
    )
  }

  return (
    <FadeSwap flag={isLoadingResult}>
      {!isLoadingResult &&
        !showCareForit &&
        !showStyleit &&
        myClosetDetail &&
        Object.keys(myClosetDetail).length && (
          <Modal
            closeButtonText="close"
            modalType="stickyRight"
            isOpen={isOpen}
            toggleModal={handleToggle}
            innerContainerClassName={styles.innerContainerCloset}
            modalHeadingClassName={styles.modalHeading}
            closeButtonSize="small"
            closeButtonClassName={styles.closeButton}
          >
            <ImageCarouselApp
              images={myClosetDetail.images}
              articleCode={myClosetDetail.code}
              shouldShowCarouselThumbnail={false}
              showFavouritesButton={false}
              slidesToShow={1}
            />
            <div className={styles.moduleContainer}>
              <HeadingMedium className={styles.myClosetDetailTitle}>
                {myClosetDetail.name}
              </HeadingMedium>
              <ul className={styles.figureDescription}>
                {myClosetDetail?.descriptionText?.map((label) => (
                  <li className={styles.figureListItem} key={label}>
                    {label}
                  </li>
                ))}
              </ul>
              {showMyclosetStyleitFlag ? (
                <>
                  <div className={styles.buttonWrapper}>
                    {careForitBtn()}
                    {styleItBtn()}
                  </div>
                  {buyItAgainBtn()}
                </>
              ) : (
                <div className={styles.buttonWrapper}>
                  {careForitBtn()}
                  {buyItAgainBtn()}
                </div>
              )}
            </div>
          </Modal>
        )}
      {showCareForit &&
        !showStyleit &&
        myClosetDetail &&
        Object.keys(myClosetDetail).length &&
        renderCareForit(
          getLabelFromStorage('mycloset.detail.careguide.title'),
          myClosetDetail.careForit,
        )}
      {!showCareForit &&
        showMyclosetStyleitFlag &&
        showStyleit &&
        myClosetDetail &&
        Object.keys(myClosetDetail).length &&
        renderStyleit(getLabelFromStorage('mycloset.painteddoor.title'))}
    </FadeSwap>
  )
}

export default MyClosetDetail
